import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { CodeStep } from "../components/CodeStep";
import { ForgotPassword } from "../components/ForgotPassword";

const HERO = "https://customer-assets.emergentagent.com/job_roma-port-crew/artifacts/2al4owka_IMG20220224180641.jpg";

export default function Login() {
  const { user, signIn } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [challenge, setChallenge] = useState(null);
  const [forgot, setForgot] = useState(false);
  const [to] = useState(() => sessionStorage.getItem("after_login") || "/");
  if (user) return <Navigate to={to} replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) return toast.error("Inserisci email e password");
    setLoading(true);
    try { const c = await signIn(email.trim(), password); if (c) setChallenge(c); else nav(to, { replace: true }); }
    catch (err) { toast.error(err.message || "Accesso non riuscito"); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.2fr_1fr] bg-[var(--navy-deep)]">
      <div className="relative hidden lg:block grain overflow-hidden">
        <img src={HERO} alt="Porto Turistico di Roma" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--navy-deep)] via-[var(--navy-deep)]/40 to-transparent" />
        <div className="absolute bottom-14 left-14 right-14 text-white fade-up">
          <p className="eyebrow !text-white/70 mb-4">Porto Turistico di Roma</p>
          <h1 className="font-display text-5xl xl:text-6xl font-extrabold leading-[1.02]" data-testid="login-slogan">Il tuo porto<br />a portata di click</h1>
          <p className="mt-5 text-white/70 max-w-md">Turni, segnalazioni, manutenzioni e comunicazioni del personale in un unico posto.</p>
        </div>
      </div>
      <div className="relative flex items-center justify-center p-6 sm:p-12">
        <Link to="/" className="absolute top-6 left-6 sm:top-8 sm:left-12 inline-flex items-center gap-2 text-sm font-semibold text-white/80 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-white rounded" data-testid="login-back-home"><ArrowLeft className="w-4 h-4" aria-hidden="true" />Torna al sito</Link>
        {challenge ? <CodeStep challenge={challenge} onBack={() => setChallenge(null)} onDone={() => nav(to, { replace: true })} /> : forgot ? <ForgotPassword initialEmail={email} onBack={() => setForgot(false)} /> : <form onSubmit={submit} className="w-full max-w-sm fade-up" data-testid="login-form">
          <div className="mb-12">
            <img src="/logo_white.png" alt="Porto Turistico di Roma" className="h-14 w-auto" data-testid="login-logo" />
            <p className="text-xs text-white/50 tracking-[0.35em] uppercase mt-3">Crew</p>
          </div>
          <p className="lg:hidden font-display text-xl font-bold text-white/80 mb-6">Il tuo porto a portata di click</p>
          <h2 className="font-display text-3xl font-bold text-white mb-2">Accedi al tuo account</h2>
          <p className="text-white/50 text-sm mb-8">Usa le stesse credenziali dell'app mobile.</p>
          <label className="text-sm text-white/70">Email</label>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="nome@portoturisticodiroma.it" className="mt-1.5 mb-5 h-12 bg-white/5 border-white/15 text-white placeholder:text-white/30" data-testid="login-email-input" />
          <label className="text-sm text-white/70">Password</label>
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 mb-8 h-12 bg-white/5 border-white/15 text-white" data-testid="login-password-input" />
          <Button type="submit" disabled={loading} className="w-full h-12 rounded-full bg-[var(--teal)] hover:bg-[#257474] text-white font-semibold" data-testid="login-submit-button">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Accedi"}
          </Button>
          <button type="button" onClick={() => setForgot(true)} className="block mx-auto mt-6 text-sm text-white/60 hover:text-white underline" data-testid="forgot-password-link">Password dimenticata?</button>
        </form>}
      </div>
    </div>
  );
}
